'use client'

import { forwardRef, ReactNode, useState, useEffect, useRef } from 'react'
import { X, AlertTriangle, Info, CheckCircle2, Undo2, Loader2 } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  componentGlass,
  typographyV3,
  componentTypography
} from '@birhaus/tokens/v3'
import { cn, ClassValue } from '@birhaus/types/utils'

/**
 * BIRHAUS Dialog V3 - Radical Minimalist Modal
 * 
 * Revolutionary modal component with:
 * - Glass morphism with backdrop blur
 * - Radical border radius (40px default)
 * - Smooth entrance/exit animations
 * - Generous spacing (2x traditional)
 * - Enhanced undo patterns
 * - Spanish-first labeling
 * - Accessibility optimized
 * - Performance optimized
 */

// Using imported cn utility from @birhaus/types/utils

export interface BirhausDialogV3Action {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  onClick: () => void | Promise<void>
  variant?: 'primary' | 'secondary' | 'destructive' | 'glass' | 'ghost'
  disabled?: boolean
  loading?: boolean
  icon?: ReactNode
}

export interface BirhausUndoConfigV3 {
  enabled: boolean
  timeoutMs?: number
  undoAction: () => void | Promise<void>
  messageEs?: string
  messageEn?: string
  labelEs?: string
  labelEn?: string
  showCountdown?: boolean
  showProgressBar?: boolean
}

export interface BirhausDialogV3Props {
  // Visibility control
  open?: boolean
  onOpenChange?: (open: boolean) => void
  
  // Content
  title?: string
  titleEs?: string
  titleEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  children?: ReactNode
  
  // V3 Design variants
  variant?: 'default' | 'glass' | 'frosted' | 'elevated' | 'minimal'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'
  radius?: keyof typeof borderRadiusV3
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  
  // V3 Visual effects
  glassMorphism?: boolean
  glow?: boolean
  float?: boolean
  blur?: number
  backdropBlur?: number
  
  // Undo functionality
  undoConfig?: BirhausUndoConfigV3
  
  // Behavior
  closeOnOverlayClick?: boolean
  closeOnEscape?: boolean
  persistent?: boolean
  preventScroll?: boolean
  
  // Actions
  primaryAction?: BirhausDialogV3Action
  secondaryActions?: BirhausDialogV3Action[]
  
  // Accessibility
  role?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  
  // Performance
  trackPerformance?: boolean
  
  // Styling
  className?: string
  overlayClassName?: string
  contentClassName?: string
}

export const BirhausDialogV3 = forwardRef<HTMLDivElement, BirhausDialogV3Props>(({
  open = false,
  onOpenChange,
  
  // Content
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  children,
  
  // V3 Design
  variant = 'glass',
  size = 'md',
  radius = 'xl',
  position = 'center',
  
  // V3 Effects
  glassMorphism = true,
  glow = false,
  float = true,
  blur = 16,
  backdropBlur = 24,
  
  // Undo functionality
  undoConfig,
  
  // Behavior
  closeOnOverlayClick = true,
  closeOnEscape = true,
  persistent = false,
  preventScroll = true,
  
  // Actions
  primaryAction,
  secondaryActions = [],
  
  // Accessibility
  role = 'dialog',
  ariaLabelledBy,
  ariaDescribedBy,
  
  // Performance
  trackPerformance = false,
  
  // Styling
  className,
  overlayClassName,
  contentClassName,
  
  ...props
}, ref) => {
  
  // BIRHAUS: State management
  const [isUndoActive, setIsUndoActive] = useState(false)
  const [undoTimeRemaining, setUndoTimeRemaining] = useState(0)
  const [isUndoing, setIsUndoing] = useState(false)
  const [isVisible, setIsVisible] = useState(open)
  const [isAnimating, setIsAnimating] = useState(false)
  
  const dialogRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayTitle = resolveText(titleEs, titleEn, title)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  
  // V3 Size classes with generous spacing
  const sizeClasses = {
    xs: 'max-w-md p-8',
    sm: 'max-w-lg p-10',
    md: 'max-w-xl p-12',
    lg: 'max-w-2xl p-16',
    xl: 'max-w-4xl p-20',
    full: 'max-w-[90vw] max-h-[90vh] p-16',
  }
  
  // V3 Variant classes with glass morphism
  const variantClasses = {
    default: `
      bg-white border border-gray-200
      shadow-elevation-3
    `,
    glass: `
      ${componentGlass.modal}
      backdrop-blur-lg
      border border-white/30
    `,
    frosted: `
      ${glassV3.effects.heavy}
      backdrop-blur-xl
      border border-white/40
    `,
    elevated: `
      bg-white
      shadow-elevation-4
      border border-gray-100
    `,
    minimal: `
      bg-white/95
      backdrop-blur-sm
      shadow-elevation-2
    `,
  }
  
  // Position classes
  const positionClasses = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-20',
    bottom: 'items-end justify-center pb-20',
    left: 'items-center justify-start pl-20',
    right: 'items-center justify-end pr-20',
  }
  
  // BIRHAUS: Undo countdown effect
  useEffect(() => {
    if (!isUndoActive || !undoConfig?.enabled) return
    
    const timeoutMs = undoConfig.timeoutMs || 5000
    let remaining = Math.ceil(timeoutMs / 1000)
    setUndoTimeRemaining(remaining)
    
    const interval = setInterval(() => {
      remaining--
      setUndoTimeRemaining(remaining)
      
      if (remaining <= 0) {
        setIsUndoActive(false)
        clearInterval(interval)
      }
    }, 1000)
    
    return () => clearInterval(interval)
  }, [isUndoActive, undoConfig])
  
  // Handle visibility changes
  useEffect(() => {
    if (open && !isVisible) {
      setIsVisible(true)
      setIsAnimating(true)
      setTimeout(() => setIsAnimating(false), 300)
    } else if (!open && isVisible) {
      setIsAnimating(true)
      setTimeout(() => {
        setIsVisible(false)
        setIsAnimating(false)
      }, 300)
    }
  }, [open, isVisible])
  
  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isVisible) return
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !persistent) {
        onOpenChange?.(false)
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [closeOnEscape, isVisible, persistent, onOpenChange])
  
  // Handle body scroll prevention
  useEffect(() => {
    if (!preventScroll || !isVisible) return
    
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [preventScroll, isVisible])
  
  // Handle undo action
  const handleUndo = async () => {
    if (!undoConfig?.undoAction || isUndoing) return
    
    setIsUndoing(true)
    try {
      await undoConfig.undoAction()
      setIsUndoActive(false)
    } catch (error) {
      console.error('Undo action failed:', error)
    } finally {
      setIsUndoing(false)
    }
  }
  
  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current && closeOnOverlayClick && !persistent) {
      onOpenChange?.(false)
    }
  }
  
  // Handle close
  const handleClose = () => {
    if (!persistent) {
      onOpenChange?.(false)
    }
  }
  
  // Handle action execution
  const executeAction = async (action: BirhausDialogV3Action) => {
    try {
      await action.onClick()
      
      // Activate undo if configured and this is a primary action
      if (action === primaryAction && undoConfig?.enabled) {
        setIsUndoActive(true)
      }
    } catch (error) {
      console.error('Action execution failed:', error)
    }
  }
  
  if (!isVisible) return null
  
  const iconVariants = {
    default: Info,
    glass: CheckCircle2,
    frosted: AlertTriangle,
    elevated: CheckCircle2,
    minimal: Info,
  }
  
  const IconComponent = iconVariants[variant]
  
  return (
    <div
      ref={overlayRef}
      className={cn(
        // Base overlay styles
        'fixed inset-0 z-50 flex',
        positionClasses[position],
        
        // Backdrop blur and background
        `backdrop-blur-[${backdropBlur}px]`,
        'bg-black/20',
        
        // Animation classes
        isAnimating && !open ? 'animate-fade-out' : 'animate-fade-in',
        
        overlayClassName
      )}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={ref || dialogRef}
        className={cn(
          // Base dialog styles
          'relative flex flex-col',
          'min-h-0 w-full',
          sizeClasses[size],
          
          // Border radius - radical v3
          `rounded-[${borderRadiusV3[radius]}]`,
          
          // Variant styling
          variantClasses[variant],
          
          // Float effect
          float && 'transform-gpu animate-bounce',
          
          // Glow effect
          glow && `shadow-[${shadowsV3.glow}]`,
          
          // Performance optimizations
          'will-change-transform',
          'backface-visibility-hidden',
          
          // Animation
          isAnimating && !open ? 'animate-scale-out' : 'animate-scale-in',
          
          contentClassName,
          className
        )}
        role={role}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-modal="true"
        {...props}
      >
        {/* Close Button */}
        {!persistent && (
          <button
            onClick={handleClose}
            className={cn(
              'absolute top-6 right-6',
              'w-10 h-10 rounded-full',
              'flex items-center justify-center',
              'text-gray-400 hover:text-gray-600',
              'hover:bg-gray-100/50',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
            )}
            aria-label="Cerrar diálogo"
          >
            <X size={20} />
          </button>
        )}
        
        {/* Header */}
        {displayTitle && (
          <div className="mb-8">
            <div className="flex items-start gap-4">
              {IconComponent && (
                <div className="flex-shrink-0 mt-1">
                  <IconComponent 
                    size={24} 
                    className={cn(
                      'text-gray-600',
                      variant === 'glass' && 'text-blue-600',
                      variant === 'frosted' && 'text-amber-600'
                    )}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 
                  id={ariaLabelledBy}
                  className={cn(
                    'text-2xl font-semibold text-gray-900 mb-2'
                  )}
                >
                  {displayTitle}
                </h2>
                {displayDescription && (
                  <p 
                    id={ariaDescribedBy}
                    className={cn(
                      'text-base text-gray-600 leading-relaxed'
                    )}
                  >
                    {displayDescription}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Content */}
        {children && (
          <div className="flex-1 mb-8">
            {children}
          </div>
        )}
        
        {/* Undo Bar */}
        {isUndoActive && undoConfig && (
          <div className={cn(
            'mb-8 p-6 rounded-2xl',
            'bg-blue-50/80 border border-blue-200/50',
            'backdrop-blur-sm',
            'animate-slide-up'
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Undo2 size={20} className="text-blue-600" />
                <span className="text-blue-900 font-medium">
                  {resolveText(
                    undoConfig.messageEs,
                    undoConfig.messageEn,
                    'Acción realizada'
                  )}
                </span>
                {undoConfig.showCountdown && (
                  <span className="text-blue-600 text-sm">
                    ({undoTimeRemaining}s)
                  </span>
                )}
              </div>
              <button
                onClick={handleUndo}
                disabled={isUndoing}
                className={cn(
                  'px-4 py-2 rounded-xl',
                  'bg-blue-600 text-white font-medium',
                  'hover:bg-blue-700',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20'
                )}
              >
                {isUndoing ? (
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Deshaciendo...</span>
                  </div>
                ) : (
                  resolveText(
                    undoConfig.labelEs,
                    undoConfig.labelEn,
                    'Deshacer'
                  )
                )}
              </button>
            </div>
            
            {/* Progress bar */}
            {undoConfig.showProgressBar && (
              <div className="mt-3 w-full bg-blue-200/50 rounded-full h-1">
                <div 
                  className="bg-blue-600 h-1 rounded-full transition-all duration-1000 ease-linear"
                  style={{
                    width: `${(undoTimeRemaining / Math.ceil((undoConfig.timeoutMs || 5000) / 1000)) * 100}%`
                  }}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        {(primaryAction || secondaryActions.length > 0) && (
          <div className="flex flex-col-reverse sm:flex-row gap-4 justify-end">
            {/* Secondary actions */}
            {secondaryActions.map((action) => (
              <button
                key={action.id}
                onClick={() => executeAction(action)}
                disabled={action.disabled || action.loading}
                className={cn(
                  // Base button styles
                  'px-8 py-4 rounded-2xl',
                  'font-medium transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                  
                  // Variant styles
                  action.variant === 'destructive' && [
                    'bg-red-50 text-red-700 border border-red-200',
                    'hover:bg-red-100 hover:border-red-300',
                  ],
                  action.variant === 'glass' && [
                    'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
                    'text-gray-700 hover:bg-white/60',
                  ],
                  (!action.variant || action.variant === 'secondary') && [
                    'bg-gray-100 text-gray-700 border border-gray-200',
                    'hover:bg-gray-200 hover:border-gray-300',
                  ],
                  
                  // Disabled state
                  (action.disabled || action.loading) && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  {action.loading && <Loader2 size={18} className="animate-spin" />}
                  {action.icon && !action.loading && action.icon}
                  <span>
                    {resolveText(action.labelEs, action.labelEn, action.label)}
                  </span>
                </div>
              </button>
            ))}
            
            {/* Primary action */}
            {primaryAction && (
              <button
                onClick={() => executeAction(primaryAction)}
                disabled={primaryAction.disabled || primaryAction.loading}
                className={cn(
                  // Base button styles
                  'px-8 py-4 rounded-2xl',
                  'font-semibold transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                  
                  // Variant styles
                  primaryAction.variant === 'destructive' && [
                    'bg-red-600 text-white',
                    'hover:bg-red-700',
                    'shadow-lg hover:shadow-xl',
                  ],
                  primaryAction.variant === 'glass' && [
                    'bg-white/50 backdrop-blur-lg saturate-[150%] border border-white/30 shadow-xl',
                    'text-gray-900 hover:bg-white/70',
                    'border border-white/30',
                  ],
                  (!primaryAction.variant || primaryAction.variant === 'primary') && [
                    'bg-blue-600 text-white',
                    'hover:bg-blue-700',
                    'shadow-lg hover:shadow-xl',
                  ],
                  
                  // Disabled state
                  (primaryAction.disabled || primaryAction.loading) && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  {primaryAction.loading && <Loader2 size={18} className="animate-spin" />}
                  {primaryAction.icon && !primaryAction.loading && primaryAction.icon}
                  <span>
                    {resolveText(primaryAction.labelEs, primaryAction.labelEn, primaryAction.label)}
                  </span>
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

BirhausDialogV3.displayName = 'BirhausDialogV3'

/**
 * Hook for managing dialog state with v3 enhancements
 */
export const useBirhausDialogV3 = (initialOpen = false) => {
  const [open, setOpen] = useState(initialOpen)
  const [isUndoActive, setIsUndoActive] = useState(false)
  
  const openDialog = () => setOpen(true)
  const closeDialog = () => setOpen(false)
  const toggleDialog = () => setOpen(!open)
  
  const activateUndo = () => setIsUndoActive(true)
  const deactivateUndo = () => setIsUndoActive(false)
  
  return {
    open,
    openDialog,
    closeDialog,
    toggleDialog,
    isUndoActive,
    activateUndo,
    deactivateUndo,
  }
}

/**
 * Helper to create dialog actions with v3 styling
 */
export const createBirhausDialogV3Action = (
  action: Omit<BirhausDialogV3Action, 'id'>
): BirhausDialogV3Action => ({
  id: crypto.randomUUID(),
  ...action,
})